import { useState, useRef, useCallback, useEffect } from 'react';
import { VoiceRecordingState, TranscriptSegment, VoiceRecorderConfig } from '../types/voice';
import { v4 as uuidv4 } from 'uuid';
import { correctVeterinaryTermsAdvanced } from '../terminology';

// Check for browser support
const SpeechRecognition = 
  (window as any).SpeechRecognition || 
  (window as any).webkitSpeechRecognition;

const defaultConfig: VoiceRecorderConfig = {
  continuous: true,
  interimResults: true,
  maxSilenceDuration: 3000,
  language: 'en-US',
  autoRestart: true,
};

export const useVoiceRecording = (config: Partial<VoiceRecorderConfig> = {}) => {
  const finalConfig = { ...defaultConfig, ...config };
  
  const [state, setState] = useState<VoiceRecordingState>({
    isRecording: false,
    isPaused: false,
    isProcessing: false,
    error: null,
    transcript: '',
    sessionId: null,
    recordingDuration: 0,
  });

  const [segments, setSegments] = useState<TranscriptSegment[]>([]);
  const recognitionRef = useRef<any>(null);
  const silenceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const pausedDurationRef = useRef<number>(0);
  const pauseStartRef = useRef<number | null>(null);

  // Initialize speech recognition
  const initializeRecognition = useCallback(() => {
    if (!SpeechRecognition) {
      setState(prev => ({ 
        ...prev, 
        error: 'Speech recognition is not supported in this browser' 
      }));
      return null;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = finalConfig.continuous;
    recognition.interimResults = finalConfig.interimResults;
    recognition.lang = finalConfig.language;
    recognition.maxAlternatives = 1;

    // Grammar enhancement could be added here if we export terms from worker
    // For now, rely on post-processing corrections

    recognition.onstart = () => {
      console.log('Voice recognition started');
      setState(prev => ({ ...prev, error: null }));
    };

    recognition.onresult = async (event: any) => {
      const results = event.results;
      const currentIndex = event.resultIndex;
      
      // Clear silence timer on new speech
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }

      // Process the current result
      const current = results[currentIndex];
      let transcriptText = current[0].transcript;
      const confidence = current[0].confidence || 0.9;
      const isFinal = current.isFinal;

      // Apply veterinary term corrections to final results
      if (isFinal) {
        try {
          const correctionResult = await correctVeterinaryTermsAdvanced(transcriptText);
          transcriptText = correctionResult.correctedText;
          
          // Log corrections if any were made
          if (correctionResult.corrections.length > 0) {
            console.log('[VoiceRecording] Applied corrections:', correctionResult.corrections);
          }
        } catch (error) {
          console.error('[VoiceRecording] Error correcting text:', error);
          // Continue with uncorrected text if correction fails
        }
      }

      // Create a new segment with corrected text
      const newSegment: TranscriptSegment = {
        id: uuidv4(),
        text: transcriptText,
        timestamp: Date.now(),
        confidence,
        isFinal,
      };

      // Update segments
      setSegments(prev => {
        // If this is an interim result, replace the last non-final segment
        if (!isFinal && prev.length > 0 && !prev[prev.length - 1].isFinal) {
          return [...prev.slice(0, -1), newSegment];
        }
        return [...prev, newSegment];
      });

      // Update full transcript (only final results with corrected text)
      if (isFinal) {
        setState(prev => ({
          ...prev,
          transcript: prev.transcript 
            ? prev.transcript + ' ' + transcriptText 
            : transcriptText,
        }));

        // Set silence timer for auto-pause
        if (finalConfig.maxSilenceDuration > 0) {
          silenceTimerRef.current = setTimeout(() => {
            pause();
          }, finalConfig.maxSilenceDuration);
        }
      }
    };

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      
      // Handle different error types
      let errorMessage = 'Voice recognition error occurred';
      switch (event.error) {
        case 'no-speech':
          errorMessage = 'No speech detected. Please try again.';
          break;
        case 'audio-capture':
          errorMessage = 'No microphone found. Please check your microphone.';
          break;
        case 'not-allowed':
          errorMessage = 'Microphone permission denied. Please allow microphone access.';
          break;
        case 'network':
          errorMessage = 'Network error. Please check your connection.';
          break;
        default:
          errorMessage = `Recognition error: ${event.error}`;
      }

      setState(prev => ({ ...prev, error: errorMessage }));

      // Auto-restart on certain errors if configured
      if (finalConfig.autoRestart && 
          ['network', 'no-speech'].includes(event.error) && 
          state.isRecording && 
          !state.isPaused) {
        setTimeout(() => {
          if (recognitionRef.current) {
            try {
              recognitionRef.current.start();
            } catch (e) {
              console.error('Failed to restart recognition:', e);
            }
          }
        }, 1000);
      }
    };

    recognition.onend = () => {
      console.log('Voice recognition ended');
      
      // Auto-restart if still recording and not paused
      if (state.isRecording && !state.isPaused && finalConfig.autoRestart) {
        try {
          recognition.start();
        } catch (e) {
          console.error('Failed to restart recognition:', e);
        }
      }
    };

    return recognition;
  }, [finalConfig, state.isRecording, state.isPaused]);

  // Start recording
  const start = useCallback(() => {
    const recognition = initializeRecognition();
    if (!recognition) return;

    recognitionRef.current = recognition;
    
    try {
      recognition.start();
      const sessionId = uuidv4();
      startTimeRef.current = Date.now();
      pausedDurationRef.current = 0;
      
      setState(prev => ({
        ...prev,
        isRecording: true,
        isPaused: false,
        sessionId,
        transcript: '',
        recordingDuration: 0,
      }));
      
      setSegments([]);

      // Start duration timer
      durationIntervalRef.current = setInterval(() => {
        if (startTimeRef.current && !pauseStartRef.current) {
          const elapsed = Date.now() - startTimeRef.current - pausedDurationRef.current;
          setState(prev => ({ ...prev, recordingDuration: elapsed }));
        }
      }, 100);
    } catch (error) {
      console.error('Failed to start recording:', error);
      setState(prev => ({ 
        ...prev, 
        error: 'Failed to start recording. Please try again.' 
      }));
    }
  }, [initializeRecognition]);

  // Pause recording
  const pause = useCallback(() => {
    if (recognitionRef.current && state.isRecording && !state.isPaused) {
      recognitionRef.current.stop();
      pauseStartRef.current = Date.now();
      
      setState(prev => ({ ...prev, isPaused: true }));
      
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }
    }
  }, [state.isRecording, state.isPaused]);

  // Resume recording
  const resume = useCallback(() => {
    if (state.isRecording && state.isPaused) {
      if (pauseStartRef.current) {
        pausedDurationRef.current += Date.now() - pauseStartRef.current;
        pauseStartRef.current = null;
      }
      
      const recognition = initializeRecognition();
      if (recognition) {
        recognitionRef.current = recognition;
        try {
          recognition.start();
          setState(prev => ({ ...prev, isPaused: false }));
        } catch (error) {
          console.error('Failed to resume recording:', error);
          setState(prev => ({ 
            ...prev, 
            error: 'Failed to resume recording. Please try again.' 
          }));
        }
      }
    }
  }, [state.isRecording, state.isPaused, initializeRecognition]);

  // Stop recording
  const stop = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }

    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }

    startTimeRef.current = null;
    pauseStartRef.current = null;
    pausedDurationRef.current = 0;
    
    setState(prev => ({
      ...prev,
      isRecording: false,
      isPaused: false,
    }));
  }, []);

  // Clear transcript
  const clearTranscript = useCallback(() => {
    setState(prev => ({ ...prev, transcript: '' }));
    setSegments([]);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, []);

  return {
    state,
    segments,
    start,
    pause,
    resume,
    stop,
    clearTranscript,
    isSupported: !!SpeechRecognition,
  };
};
